import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { MODULE_CONFIG } from "./shared";
import { capitalize, formatDate, formatTime } from "../../methods/methods";
import {
  FiArrowLeft,
  FiEdit3,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiCheck,
  FiX,
  FiDollarSign,
  FiSliders,
  FiShield,
  FiList,
  FiCreditCard,
  FiCalendar,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";

export default function PlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inquiry, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch Plan Detail with platform loader
  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    loader(true);

    try {
      const res = await ApiClient.get(`${MODULE_CONFIG.endpoints.detail}?id=${id}`);
      if (res?.success && res?.data) {
        setPlan(res.data);
      } else {
        toast.error("Subscription plan record not found.");
        navigate(MODULE_CONFIG.routeBase);
      }
    } catch (err) {
      console.error("Failed to load plan detail:", err);
      toast.error("Failed to fetch plan details.");
    } finally {
      setLoading(false);
      loader(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Status Toggle with confirmation
  const handleToggleStatus = () => {
    if (!inquiry) return;
    const isCurrentlyActive = inquiry.status === "active";
    const nextStatus = isCurrentlyActive ? "deactive" : "active";

    swal({
      title: `${isCurrentlyActive ? "Deactivate" : "Activate"} Plan?`,
      text: `Are you sure you want to mark "${inquiry.name}" as ${nextStatus}?`,
      icon: "warning",
      buttons: ["Cancel", "Yes, Proceed"],
      dangerMode: isCurrentlyActive,
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.put(MODULE_CONFIG.endpoints.statusChange, {
          id: id,
          status: nextStatus,
        });
        if (res?.success) {
          toast.success(`Plan marked as ${capitalize(nextStatus)}.`);
          setPlan((prev) => (prev ? { ...prev, status: nextStatus } : prev));
        }
      } catch (err) {
        console.error("Failed to toggle status:", err);
      } finally {
        loader(false);
      }
    });
  };

  // Delete Plan
  const handleDelete = () => {
    if (!inquiry) return;
    swal({
      title: "Delete Subscription Plan?",
      text: `Are you sure you want to delete "${inquiry.name}"? This action cannot be undone.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.delete(`${MODULE_CONFIG.endpoints.delete}?id=${id}`);
        if (res?.success) {
          toast.success("Subscription plan deleted successfully.");
          navigate(MODULE_CONFIG.routeBase);
        }
      } catch (err) {
        console.error("Failed to delete plan:", err);
      } finally {
        loader(false);
      }
    });
  };

  if (loading && !inquiry) {
    return (
      <Layout>
        <div className="py-24 text-center">
          <div className="w-8 h-8 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs font-bold text-slate-900">Loading Plan Details...</p>
        </div>
      </Layout>
    );
  }

  if (!inquiry) return null;

  const statusInfo =
    MODULE_CONFIG.statusMap[inquiry.status] || MODULE_CONFIG.statusMap.active;
  const integrationInfo =
    MODULE_CONFIG.integrationMap[inquiry.quickbooks_integration] ||
    MODULE_CONFIG.integrationMap.NONE;
  const isFree = inquiry.type === "free" || Number(inquiry.price) === 0;

  return (
    <Layout>
      <div className="w-full max-w-full space-y-6 pb-16 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & ACTIONS (HIGH CONTRAST, ZERO LIGHT GREY) */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5 min-w-0">
            <Link
              to={MODULE_CONFIG.routeBase}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs shrink-0 flex items-center justify-center"
              title="Back to Plans"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </Link>
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
                  {inquiry.name}
                </h1>
                <button
                  type="button"
                  onClick={handleToggleStatus}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border whitespace-nowrap cursor-pointer hover:shadow-xs hover:scale-105 active:scale-95 transition-all ${statusInfo.bgClass}`}
                  title={
                    inquiry.status === "active"
                      ? "Click to deactivate plan"
                      : "Click to activate plan"
                  }
                >
                  <span className={`w-2 h-2 rounded-full ${statusInfo.dotClass}`} />
                  {statusInfo.label}
                </button>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${integrationInfo.badgeClass}`}
                >
                  <HiOutlineBuildingOffice2 className="w-3.5 h-3.5" />
                  <span>{integrationInfo.label}</span>
                </span>
              </div>
              <p className="text-xs text-slate-800 font-bold">
                Plan Slug: <span className="font-mono text-slate-950 font-black">{inquiry.slug || "—"}</span>
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            <button
              type="button"
              onClick={handleToggleStatus}
              className={`inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl text-xs font-bold transition-all border cursor-pointer shadow-xs ${
                inquiry.status === "active"
                  ? "bg-slate-100 hover:bg-slate-200 text-slate-800 border-slate-300"
                  : "bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300"
              }`}
            >
              {inquiry.status === "active" ? (
                <>
                  <FiXCircle className="w-4 h-4 text-slate-700" />
                  <span>Deactivate Plan</span>
                </>
              ) : (
                <>
                  <FiCheckCircle className="w-4 h-4 text-emerald-700" />
                  <span>Activate Plan</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`)}
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98]"
            >
              <FiEdit3 className="w-3.5 h-3.5" />
              <span>Edit Plan</span>
            </button>

            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl border border-rose-400 bg-rose-50 text-rose-800 hover:bg-rose-100 text-xs font-bold transition-all cursor-pointer shadow-xs"
              title="Delete Plan"
            >
              <FiTrash2 className="w-4 h-4 text-rose-700" />
              <span>Delete</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. MAIN DOSSIER GRID (HIGH-CONTRAST CARDS & PANELS) */}
        {/* ======================================================== */}
        {/* ======================================================== */}
        {/* 2. MAIN DOSSIER GRID (HIGH-CONTRAST CARDS & PANELS) */}
        {/* ======================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          {/* LEFT COLUMN: Overview & Pricing (7 cols) */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            {/* Card: Pricing & Terms */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-5">
              <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center font-bold">
                  <FiDollarSign className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Pricing & Commercial Terms
                </h3>
              </div>

              {/* 3 Metric High-Contrast Blocks */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                <div className="bg-blue-50/30 p-4 rounded-xl border border-slate-300">
                  <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider">
                    Monthly Price:
                  </span>
                  <p className="text-xl font-black text-slate-950 mt-1">
                    {isFree ? "Free" : `$${inquiry.price}`}
                  </p>
                </div>

                <div className="bg-blue-50/30 p-4 rounded-xl border border-slate-300">
                  <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider">
                    Free Trial:
                  </span>
                  <p className="text-sm font-black text-slate-950 mt-1.5">
                    {Number(inquiry.freeDays) > 0 ? `${inquiry.freeDays} Days` : "No Free Trial"}
                  </p>
                </div>

                <div className="bg-blue-50/30 p-4 rounded-xl border border-slate-300">
                  <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider">
                    Plan Tier:
                  </span>
                  <p className="text-sm font-black text-slate-950 capitalize mt-1.5">
                    {inquiry.type || "paid"} Tier
                  </p>
                </div>
              </div>

              {/* Description Panel */}
              <div>
                <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider mb-1.5">
                  Plan Description & Scope:
                </span>
                <div className="bg-white p-4 rounded-xl border border-slate-300 text-xs font-semibold text-slate-900 leading-relaxed">
                  {inquiry.description || "No description provided for this plan."}
                </div>
              </div>

              {/* Stripe Identifiers (High Contrast) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                <div className="border border-slate-300 rounded-xl p-3.5 bg-blue-50/20">
                  <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider">
                    Stripe Product ID:
                  </span>
                  <p className="font-mono text-xs font-bold text-slate-950 truncate mt-1">
                    {inquiry.stripe_product_id || "Not configured"}
                  </p>
                </div>

                <div className="border border-slate-300 rounded-xl p-3.5 bg-blue-50/20">
                  <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider">
                    Stripe Price ID:
                  </span>
                  <p className="font-mono text-xs font-bold text-slate-950 truncate mt-1">
                    {inquiry.stripe_price_id || "Not configured"}
                  </p>
                </div>
              </div>
            </div>

            {/* Card: Platform Limits & Quotas (Dynamic from API) */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-5 flex-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center justify-center font-bold">
                    <FiSliders className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Account Limits & Quotas
                  </h3>
                </div>

                <div className="pt-2">
                  {inquiry.limits && Object.keys(inquiry.limits).length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3.5">
                      {Object.entries(inquiry.limits).flatMap(([key, val]) => {
                        if (typeof val === "object" && val !== null) {
                          return Object.entries(val).map(([subKey, subVal]) => (
                            <div
                              key={subKey}
                              className="p-3.5 rounded-xl bg-blue-50/25 border border-slate-300 text-center"
                            >
                              <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider truncate">
                                {subKey.replace(/_/g, " ").replace(/^max /, "Max ")}
                              </span>
                              <p className="text-base font-black text-slate-950 mt-1">
                                {subVal === -1 ? "Unlimited" : subVal ?? "—"}
                              </p>
                            </div>
                          ));
                        }
                        return [
                          <div
                            key={key}
                            className="p-3.5 rounded-xl bg-blue-50/25 border border-slate-300 text-center"
                          >
                            <span className="text-slate-800 font-extrabold block text-[11px] uppercase tracking-wider truncate">
                              {key.replace(/_/g, " ").replace(/^max /, "Max ")}
                            </span>
                            <p className="text-base font-black text-slate-950 mt-1">
                              {val === -1 ? "Unlimited" : val ?? "—"}
                            </p>
                          </div>,
                        ];
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-800 font-bold italic">
                      No account limits specified for this plan.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Marketing Display Features & Metadata (5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-6 h-full">
            {/* Display Features Bullet Points (flex-1 with vertical scroll if data is high) */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex-1 flex flex-col justify-between min-h-0">
              <div>
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center font-bold">
                    <FiList className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                      Customer Display Features
                    </h3>
                    <p className="text-[11px] font-bold text-slate-800">
                      Pricing matrix highlights & bullet points
                    </p>
                  </div>
                </div>

                <div className="space-y-2.5 pt-4 overflow-y-auto max-h-[300px] pr-1 scrollbar">
                  {Array.isArray(inquiry.display_features) && inquiry.display_features.length > 0 ? (
                    inquiry.display_features.map((df, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-2.5 p-2.5 sm:p-3 rounded-xl border text-xs transition-colors shadow-xs ${
                          df.included
                            ? "bg-white border-slate-300"
                            : "bg-slate-50 border-slate-200"
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 shadow-xs ${
                            df.included
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-400 text-white"
                          }`}
                        >
                          {df.included ? (
                            <FiCheck className="w-3 h-3 stroke-[3]" />
                          ) : (
                            <FiX className="w-3 h-3 stroke-[3]" />
                          )}
                        </span>
                        <span
                          className={`${
                            df.included
                              ? "font-extrabold text-slate-950"
                              : "font-bold text-slate-500 line-through"
                          }`}
                        >
                          {df.title}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-800 font-bold italic p-3">
                      No marketing bullet points configured for this plan.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Metadata / Audit Card */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4 text-xs shrink-0">
              <div className="flex items-center gap-2 text-slate-900 font-black pb-3 border-b border-slate-200">
                <FiCalendar className="w-4 h-4 text-slate-800" />
                <span className="uppercase tracking-wide text-xs">Audit & Record Details</span>
              </div>
              <div className="space-y-3 font-semibold">
                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                  <span className="text-slate-800 font-extrabold text-[11px] uppercase tracking-wider">
                    Created:
                  </span>
                  <span className="font-black text-slate-950">
                    {formatDate(inquiry.createdAt)} at {formatTime(inquiry.createdAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                  <span className="text-slate-800 font-extrabold text-[11px] uppercase tracking-wider">
                    Last Updated:
                  </span>
                  <span className="font-black text-slate-950">
                    {formatDate(inquiry.updatedAt)} at {formatTime(inquiry.updatedAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-800 font-extrabold text-[11px] uppercase tracking-wider">
                    Plan Record ID:
                  </span>
                  <span className="font-mono text-xs text-slate-950 font-black truncate max-w-[190px]">
                    {inquiry._id || inquiry.id}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
